import { useTranslation } from 'react-i18next';
import type { CoverageBoardVM } from './useCoverageBoard';
import type { MatrixCell } from './types';

/**
 * EAA-1817: "Bảng phủ vai trò" — the role x application matrix. A real
 * `<table>` (not a div-grid) so `th scope="row"/"col"` gives a screen reader
 * the app/role pairing for free; each cell is a `<button>` so the whole
 * board is keyboard-reachable via Tab/Shift+Tab and activates on
 * Enter/Space with no extra key handling.
 *
 * Cell states are rendered EXACTLY as the server resolves them
 * (`CoverageCellState` — see `RoleMapCoverage` docstring for the
 * paused-wins-over-everything precedence); this component never
 * recomputes a cell's health.
 */
export function CoverageMatrixCard({ vm }: { vm: CoverageBoardVM }) {
    const { t } = useTranslation();

    // EAA-1845: built inside the component (not module scope) so the labels
    // can go through t() — this used to be a plain module-level constant.
    const cellStateText: Record<MatrixCell['state'], string> = {
        linked: t('console.roleCoverage.cellState.linked'),
        multi: t('console.roleCoverage.cellState.multi'),
        paused: t('console.roleCoverage.cellState.paused'),
        none: t('console.roleCoverage.cellState.none'),
    };

    return (
        <section
            className="hz-card rc-matrix-card"
            aria-label={t('console.roleCoverage.matrix.ariaLabel')}
        >
            <div className="rc-matrix-card-head">
                <h2 className="rc-card-title">{t('console.roleCoverage.matrix.title')}</h2>
                <p className="rc-card-subtitle">
                    {t('console.roleCoverage.matrix.subtitle', {
                        appCount: vm.matrixRows.length,
                        roleKeyCount: vm.roleKeys.length,
                    })}
                </p>
            </div>

            {vm.roleKeys.length === 0 || vm.matrixRows.length === 0 ? (
                <p className="portal-admin-empty">{t('console.roleCoverage.matrix.empty')}</p>
            ) : (
                <div className="portal-table-scroll">
                    <table className="rc-matrix">
                        <caption className="portal-sr-only">
                            {t('console.roleCoverage.matrix.captionSrOnly')}
                        </caption>
                        <thead>
                            <tr>
                                <th scope="col" className="rc-matrix-corner" />
                                {vm.roleKeys.map((roleKey) => (
                                    <th key={roleKey} scope="col" className="rc-matrix-col-head">
                                        {roleKey}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {vm.matrixRows.map(({ app, cells }) => (
                                <tr
                                    key={app.code}
                                    className={
                                        app.code === vm.selectedAppCode
                                            ? 'rc-matrix-row--selected'
                                            : ''
                                    }
                                >
                                    <th scope="row" className="rc-matrix-row-head">
                                        {app.name}
                                    </th>
                                    {cells.map((cell) => (
                                        <td key={cell.roleKey}>
                                            <button
                                                type="button"
                                                className={`rc-matrix-cell rc-matrix-cell--${cell.state}`}
                                                onClick={() => vm.selectCell(cell)}
                                                aria-label={
                                                    cell.count > 1
                                                        ? t(
                                                              'console.roleCoverage.matrix.cellAriaLabelWithCount',
                                                              {
                                                                  appName: app.name,
                                                                  roleKey: cell.roleKey,
                                                                  stateText: cellStateText[
                                                                      cell.state
                                                                  ],
                                                                  count: cell.count,
                                                              }
                                                          )
                                                        : t(
                                                              'console.roleCoverage.matrix.cellAriaLabel',
                                                              {
                                                                  appName: app.name,
                                                                  roleKey: cell.roleKey,
                                                                  stateText: cellStateText[
                                                                      cell.state
                                                                  ],
                                                              }
                                                          )
                                                }
                                            >
                                                {cell.state === 'multi' && (
                                                    <span aria-hidden="true">{cell.count}</span>
                                                )}
                                                {cell.state === 'paused' && (
                                                    <span aria-hidden="true">!</span>
                                                )}
                                            </button>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="rc-matrix-footer">
                <ul className="rc-matrix-legend">
                    <li className="rc-matrix-legend-item">
                        <span
                            className="rc-matrix-legend-swatch rc-matrix-cell--linked"
                            aria-hidden="true"
                        />
                        {cellStateText.linked}
                    </li>
                    <li className="rc-matrix-legend-item">
                        <span
                            className="rc-matrix-legend-swatch rc-matrix-cell--multi"
                            aria-hidden="true"
                        />
                        {cellStateText.multi}
                    </li>
                    <li className="rc-matrix-legend-item">
                        <span
                            className="rc-matrix-legend-swatch rc-matrix-cell--paused"
                            aria-hidden="true"
                        />
                        {cellStateText.paused}
                    </li>
                    <li className="rc-matrix-legend-item">
                        <span
                            className="rc-matrix-legend-swatch rc-matrix-cell--none"
                            aria-hidden="true"
                        />
                        {cellStateText.none}
                    </li>
                </ul>
                <p className="rc-card-note">{t('console.roleCoverage.matrix.footerNote')}</p>
            </div>
        </section>
    );
}

export default CoverageMatrixCard;
